import React from 'react';
import Icon from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import cn from '@/utils/cn';
import { useSPAThemeScope } from './SPAThemeScope';

// Important: react-toastify's stylesheet paints .Toastify__toast--default white with #aaa text at
// the same specificity as a utility class, and loads after Tailwind (ui/Toast wins the rest).
const toastClass = cn(
  'tw-box-border tw-border tw-border-solid tw-border-spa-border',
  '!tw-bg-spa-bg-secondary !tw-text-spa-primary',
);

const CloseButton = ({ closeToast }: { closeToast?: () => void }) => (
  <button
    aria-label="Close notification"
    className={cn(
      'tw-ml-auto tw-shrink-0 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0',
      'tw-text-lg tw-leading-none tw-text-spa-tertiary hover:tw-text-spa-heading',
    )}
    type="button"
    onClick={closeToast}
  >
    &#x2715;
  </button>
);

const SuccessContent = ({ message }: { message: string }) => (
  <span className="tw-flex tw-items-center tw-gap-3">
    <span
      className={cn(
        'tw-inline-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full',
        'tw-bg-[color:color-mix(in_srgb,var(--spa-text-success)_15%,transparent)] tw-text-spa-success',
      )}
    >
      <Icon fill="currentColor" icon="Check" size={12} />
    </span>
    <span>{message}</span>
  </span>
);

// The SPA face of ui/Toast: same react-toastify plumbing, SPA tokens. Toasts portal to
// document.body, so a legacy mount's pinned theme class travels with them via SPAThemeScope.
export const useSPAToast = () => {
  const themeScope = useSPAThemeScope();

  return {
    success: (message: string) => toast.show(<SuccessContent message={message} />, {
      className: cn(toastClass, themeScope),
      closeButton: <CloseButton />,
    }),
  };
};

export default useSPAToast;
